import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import { signatureTrackingService } from '../../services/signatureTrackingService';
import { useAuth } from '../../contexts/AuthContext';

const SignatureTracking = () => {
  const { user } = useAuth();
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [escalationHistory, setEscalationHistory] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [overdueAlerts, setOverdueAlerts] = useState([]);

  const handleRealtimeUpdate = (eventType, data) => {
    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      loadSignatureRequests();
      loadData();
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadData();
      checkOverdueSignatures();

      // Setup real-time subscription
      const channel = signatureTrackingService?.subscribeToSignatureRequests(
        user?.id,
        handleRealtimeUpdate
      );

      return () => {
        signatureTrackingService?.unsubscribeFromSignatureRequests(channel);
      };
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      loadSignatureRequests();
    }
  }, [activeFilter, user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [requestsData, statsData] = await Promise.all([
        signatureTrackingService?.getSignatureRequests(),
        signatureTrackingService?.getSignatureStats()
      ]);
      setSignatureRequests(requestsData);
      setStats(statsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSignatureRequests = async () => {
    try {
      const filters = {};
      if (activeFilter !== 'all') {
        if (activeFilter === 'pending') filters.status = 'pending';
        if (activeFilter === 'signed') filters.status = 'signed';
        if (activeFilter === 'overdue') filters.overdue = true;
        if (activeFilter === 'client') filters.signerType = 'client';
        if (activeFilter === 'staff') filters.signerType = 'staff';
        if (activeFilter === 'escalated') filters.escalationLevel = 'first';
      }
      const data = await signatureTrackingService?.getSignatureRequests(filters);
      setSignatureRequests(data);
    } catch (err) {
      setError(err?.message);
    }
  };

  const checkOverdueSignatures = async () => {
    try {
      const alerts = await signatureTrackingService?.checkOverdueSignatures();
      setOverdueAlerts(alerts);
    } catch (err) {
      console.error('Error checking overdue signatures:', err);
    }
  };

  const handleSendReminder = async (requestId) => {
    try {
      await signatureTrackingService?.sendReminder(requestId, 'manual');
      alert('تم إرسال التذكير بنجاح');
      loadSignatureRequests();
    } catch (err) {
      alert(err?.message);
    }
  };

  const handleViewDetails = async (request) => {
    try {
      setSelectedRequest(request);
      const [reminders, escalations] = await Promise.all([
        signatureTrackingService?.getReminderHistory(request?.id),
        signatureTrackingService?.getEscalationHistory(request?.id)
      ]);
      setReminderHistory(reminders);
      setEscalationHistory(escalations);
      setShowDetailsModal(true);
    } catch (err) {
      alert(err?.message);
    }
  };

  const handleEscalate = async (escalationData) => {
    try {
      await signatureTrackingService?.escalateRequest(selectedRequest?.id, escalationData);
      alert('تم تصعيد الطلب بنجاح');
      setShowEscalateModal(false);
      loadSignatureRequests();
      checkOverdueSignatures();
    } catch (err) {
      alert(err?.message);
    }
  };

  const actionBoxes = [
    {
      id: 'all',
      icon: 'FileSignature',
      label: 'جميع التوقيعات',
      count: stats?.total || 0,
      color: '#3B82F6',
      bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.5)',
      onClick: () => setActiveFilter('all')
    },
    {
      id: 'pending',
      icon: 'Clock',
      label: 'قيد الانتظار',
      count: stats?.pending || 0,
      color: '#F59E0B',
      bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.5)',
      onClick: () => setActiveFilter('pending')
    },
    {
      id: 'overdue',
      icon: 'AlertCircle',
      label: 'متأخر',
      count: stats?.overdue || 0,
      color: '#EF4444',
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(248, 113, 113, 0.25) 100%)',
      borderColor: 'rgba(239, 68, 68, 0.5)',
      onClick: () => setActiveFilter('overdue')
    },
    {
      id: 'client',
      icon: 'Users',
      label: 'توقيعات العملاء',
      count: stats?.clientPending || 0,
      color: '#8B5CF6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      onClick: () => setActiveFilter('client')
    },
    {
      id: 'staff',
      icon: 'UserCheck',
      label: 'توقيعات الموظفين',
      count: stats?.staffPending || 0,
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.5)',
      onClick: () => setActiveFilter('staff')
    },
    {
      id: 'escalated',
      icon: 'TrendingUp',
      label: 'مصعّد',
      count: stats?.escalated || 0,
      color: '#DC2626',
      bgGradient: 'linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(239, 68, 68, 0.25) 100%)',
      borderColor: 'rgba(220, 38, 38, 0.5)',
      onClick: () => setActiveFilter('escalated')
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      signed: '#10B981',
      rejected: '#EF4444',
      expired: '#6B7280'
    };
    return colors?.[status] || '#6B7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'قيد الانتظار',
      signed: 'موقّع',
      rejected: 'مرفوض',
      expired: 'منتهي'
    };
    return labels?.[status] || status;
  };

  const getSignerTypeLabel = (type) => {
    const labels = {
      client: 'عميل',
      staff: 'موظف',
      witness: 'شاهد'
    };
    return labels?.[type] || type;
  };

  const getEscalationLevelLabel = (level) => {
    const labels = {
      none: 'لا يوجد',
      first: 'المستوى الأول',
      second: 'المستوى الثاني',
      final: 'المستوى النهائي'
    };
    return labels?.[level] || level;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('ar-JO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout onQuickMenuToggle={() => {}}>
      <SubHeader
        title="تتبع التوقيعات"
        description="متابعة حالة التوقيعات وإدارة التذكيرات والتصعيد"
        icon="FileSignature"
        iconColor="#06B6D4"
      />

      {/* Overdue Alerts Banner */}
      {overdueAlerts?.length > 0 && (
        <div className="mb-6 bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={24} color="#EF4444" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-600 mb-2">
                تنبيه: {overdueAlerts?.length} توقيع متأخر
              </h3>
              <div className="space-y-2">
                {overdueAlerts?.slice(0, 3)?.map((alert) => (
                  <div key={alert?.id} className="text-sm text-foreground">
                    <span className="font-medium">{alert?.contractNumber}</span> - 
                    {alert?.signerName} ({getSignerTypeLabel(alert?.signerType)}) - 
                    متأخر {alert?.daysOverdue} يوم
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Boxes */}
      <ActionBoxes 
        actions={actionBoxes} 
        showNewContractModal={false}
        setShowNewContractModal={() => {}}
      />

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">معدل الإنجاز</p>
                <p className="text-2xl font-bold text-foreground">{stats?.completionRate}%</p>
              </div>
              <Icon name="TrendingUp" size={32} color="#10B981" />
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">موقّع</p>
                <p className="text-2xl font-bold text-green-600">{stats?.signed}</p>
              </div>
              <Icon name="CheckCircle" size={32} color="#10B981" />
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">قيد الانتظار</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.pending}</p>
              </div>
              <Icon name="Clock" size={32} color="#F59E0B" />
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">متأخر</p>
                <p className="text-2xl font-bold text-red-600">{stats?.overdue}</p>
              </div>
              <Icon name="AlertCircle" size={32} color="#EF4444" />
            </div>
          </div>
        </div>
      )}

      {/* Signature Requests Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">طلبات التوقيع</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">جاري التحميل...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <Icon name="AlertCircle" size={48} color="#EF4444" />
            <p className="text-red-600 mt-2">{error}</p>
          </div>
        ) : signatureRequests?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileSignature" size={48} color="#9CA3AF" />
            <p className="text-muted-foreground mt-2">لا توجد طلبات توقيع</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    رقم العقد
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    الموقّع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    النوع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    تاريخ الاستحقاق
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    التذكيرات
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    التصعيد
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {signatureRequests?.map((request) => (
                  <tr key={request?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-foreground">
                        {request?.contractNumber}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {request?.contractType}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-foreground">{request?.signerName}</div>
                      <div className="text-xs text-muted-foreground">{request?.signerEmail}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getSignerTypeLabel(request?.signerType)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(request?.status)}20`,
                          color: getStatusColor(request?.status)
                        }}
                      >
                        {getStatusLabel(request?.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-foreground">{formatDate(request?.dueDate)}</div>
                      {request?.isOverdue && (
                        <div className="text-xs text-red-600 font-medium">
                          متأخر {request?.daysOverdue} يوم
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-foreground">{request?.reminderCount} تذكير</div>
                      {request?.lastReminderSentAt && (
                        <div className="text-xs text-muted-foreground">
                          آخر: {formatDate(request?.lastReminderSentAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {request?.escalationLevel !== 'none' ? (
                        <div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {getEscalationLevelLabel(request?.escalationLevel)}
                          </span>
                          {request?.escalatedToName && (
                            <div className="text-xs text-muted-foreground mt-1">
                              إلى: {request?.escalatedToName}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">لا يوجد</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(request)}
                        >
                          <Icon name="Eye" size={16} />
                        </Button>
                        {request?.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendReminder(request?.id)}
                            >
                              <Icon name="Bell" size={16} />
                            </Button>
                            {request?.isOverdue && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowEscalateModal(true);
                                }}
                              >
                                <Icon name="TrendingUp" size={16} />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">تفاصيل طلب التوقيع</h3>
              <button onClick={() => setShowDetailsModal(false)}>
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">رقم العقد</p>
                  <p className="text-base font-medium text-foreground">{selectedRequest?.contractNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">الموقّع</p>
                  <p className="text-base font-medium text-foreground">{selectedRequest?.signerName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">الحالة</p>
                  <p className="text-base font-medium text-foreground">{getStatusLabel(selectedRequest?.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">تاريخ الاستحقاق</p>
                  <p className="text-base font-medium text-foreground">{formatDate(selectedRequest?.dueDate)}</p>
                </div>
              </div>

              {/* Reminder History */}
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">سجل التذكيرات</h4>
                {reminderHistory?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">لا توجد تذكيرات</p>
                ) : (
                  <div className="space-y-2">
                    {reminderHistory?.map((reminder) => (
                      <div key={reminder?.id} className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            {reminder?.reminderType}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(reminder?.sentAt)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{reminder?.messageContent}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Escalation History */}
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">سجل التصعيد</h4>
                {escalationHistory?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">لا توجد تصعيدات</p>
                ) : (
                  <div className="space-y-2">
                    {escalationHistory?.map((escalation) => (
                      <div key={escalation?.id} className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            {getEscalationLevelLabel(escalation?.escalationLevel)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(escalation?.escalatedAt)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          من: {escalation?.escalatedFromName} إلى: {escalation?.escalatedToName}
                        </p>
                        <p className="text-sm text-foreground mt-1">{escalation?.reason}</p>
                        {escalation?.daysOverdue > 0 && (
                          <p className="text-xs text-red-600 mt-1">
                            متأخر {escalation?.daysOverdue} يوم
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Escalate Modal */}
      {showEscalateModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">تصعيد الطلب</h3>
              <button onClick={() => setShowEscalateModal(false)}>
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-muted-foreground mb-4">
                سيتم تصعيد هذا الطلب إلى المستوى الأعلى للمتابعة
              </p>
              <Button
                onClick={() => handleEscalate({
                  escalatedTo: user?.id,
                  reason: `تأخر في التوقيع - ${selectedRequest?.daysOverdue} يوم`,
                  level: selectedRequest?.escalationLevel === 'none' ? 'first' : 'second'
                })}
                className="w-full"
              >
                تأكيد التصعيد
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SignatureTracking;